import axios from "axios";
import uuidv4 from "uuid/v4";
import nes from "nes";
import Hydrater from "./hydrater";
import WsClient from "./ws-client";
import {ServicesRegistry} from "./services-registry.lib";
import qs from 'qs';
import { EnvConfig } from "../env-config";
import LStorage from "~/lib/storage";

let _router = null;
let _store = null;

const srvRegistry: ServicesRegistry = ServicesRegistry.getInstance();

export const initServicesManager = ({ router, store }) => {
  _store = store;
  _router = router;
};

export const getService = (name, ...params) => srvRegistry.get(name, ...params);
export const getUninstantiatedWebsocketServicesNames = () => srvRegistry.getAllUninstantiatedServicesNames('websocket');
export const getInstantiatedWebsocketServicesNames = () => srvRegistry.getAllInstantiatedServicesNames('websocket');

// Create uninstantiated service and will be automaticcaly instanciated after authentification
export const createWSService = ({ name, interceptAuth, rootPath, server }): ({ token, headers }) => WsClient => {
  const service = (opts?: { token: string, headers: object }): WsClient => {
    const params = {
      rootPath,
      token: null,
      headers: opts?.headers || {},
      noAuth: !interceptAuth
    };

    if (interceptAuth) {
      if (opts?.token) params.token = opts?.token;
    }

    return new WsClient(name, server, params);
  };

  srvRegistry.add(name, service);
  srvRegistry.setType(name, 'websocket');

  return service;
};

export const createService = ({ name, root, deffered = null, interceptAuth = true, headers = {} }) => {
  if (srvRegistry.has(name)) return srvRegistry.get(name);

  if (!root) throw new Error("You should define a root for you services");
  if (!root.endsWith("/")) root = `${root}/`;

  let service = axios.create({ baseURL: root, headers, paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) });

  if (interceptAuth) {
    service.interceptors.request.use(
      config => {
        if (! config.headers?.hasOwnProperty("Authorization") && _store.state.auth.token) {
          config.headers.Authorization = `Bearer ${_store.state.auth.token}`;
        }
        return config;
      },
      err => Promise.reject(err)
    );
  }

  service.interceptors.response.use(
    response => response,
    error => Promise.reject(error.response)
  );

  if (deffered) {
    service = bindDeferred(service, deffered);
  }

  service.interceptors.response.use(
    (response) => {
      return response;
    },
    async (e) => {
      const originalRequest = e.config;

      const refreshToken = LStorage.get('refreshToken');
      const sessionUuid =  LStorage.get('sessionUuid');

      if (checkIfTokenNeedRefresh(sessionUuid, refreshToken, originalRequest.retry, e.status)) {
        originalRequest.retry = true;
        return axios.post(`${EnvConfig("AUTH_SERVICE_URL")}/auth/refresh-token`, { refreshToken, sessionUuid }).then((res) => {
          if (res.status === 200) {

            // we replace the token on the store/local storage with the new valid token
            _store.state.auth.token = res.data.token;
            LStorage.set("token", res.data.token);

            // we change the token on the request which has an expirated token
            originalRequest.headers.Authorization = `Bearer ${res.data.token}`;

            // we retry the original request
            return axios(originalRequest);
          }
          return Promise.reject(e);
        });
      }
      return Promise.reject(e);
    }
  );

  srvRegistry.add(name, service, true);

  return service;
};


function bindDeferred(service, url) {

  service.deffered = (uuid = undefined) => {

    uuid = uuid || uuidv4();

    const handler = (target, proxyFn) => {

      return async (...args) => {

        if ( ! args.length) {
          throw new Error(`deffered requiring 1+ argument(s)`);
        }

        const [endpoint, ...params] = args;
        const prefix = endpoint.indexOf("?") === -1 ? "?" : "&";
        const uri = `${endpoint}${prefix}_cid_=${uuid}&async=1`;

        let restRes = await proxyFn.apply(
          target,
          [uri, ...params]
        );

        if (restRes.data.ok && restRes.data.exchange) {

          return new Promise(async (res, rej) => {
            try {

              const path = `/${restRes.data.exchange}/${uuid}`;
              const client = new nes.Client(url);
              const hydrater = new Hydrater();
              await client.connect();
              await client.subscribe(path, async ({data, status = {hydrating: false, fullfilled: true}}) => {
                try {
                  if (!data || data.isBoom) {
                    throw new Error(data.output);
                  }
                  if (!status.fullfilled) {
                    hydrater.binded && hydrater.post(data);
                    res({
                      data,
                      hydrater: status.fullfilled ? undefined : hydrater
                    });
                  } else {
                    hydrater.close();
                    res({data});
                  }
                } catch (e) {
                  rej(e);
                } finally {
                  if (status.fullfilled) {
                    await client.unsubscribe(path);
                    client.disconnect();
                  }
                }
              });
            } catch (e) {
              rej(e);
            }

          });

        }

        return restRes.data;
      };
    };
    return {
      get: handler(service, service.get),
      post: handler(service, service.post),
      put: handler(service, service.put),
      delete: handler(service, service.delete)
    };
  };

  return service;
}


export function wrapXhrError(serviceFn) {
  return async function wrappedService(...args) {
    try {
      return await serviceFn(...args)
    } catch (err) {
      if (err.status === 400) {
        return _router.push("/400");
      } else if (err.status === 403) {
        return _router.push("/403");
      } else if (err.status === 404) {
        return _router.push("/404");
      } else if (err.status === 500) {
        return _router.push("/500");
      } else {
        throw new Error(err);
      }
    }
  }
}

function checkIfTokenNeedRefresh(sessionUuid: string, refreshToken: string, retry: boolean, requestStatus: number) {
  // we check if sessionUuid and refresh token are not null
  // if we already test to retry the request for avoid infinite loop
  // we try to refresh the token only on 401
  if(sessionUuid && refreshToken && !retry && requestStatus === 401) return true;

  return false;
};